import { useState, useEffect, useLayoutEffect } from "react";
import classes from "./style.module.scss";
import SliderView from "./SliderView";
import { useDispatch, useSelector } from "react-redux";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import Tooltip from "@mui/material/Tooltip";
import { useRef } from "react";
import { MdOutlineIosShare } from "react-icons/md";
import ProviderComment from "./provider-comment/ProviderComment";
import LaunchTwoToneIcon from "@mui/icons-material/LaunchTwoTone";
import LikeDislike from "./like-dislike/LikeDislike";
import ReferralPopup from "./ReferralPopup/ReferralPopup";
import Button from "@mui/material/Button";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import AiVerifyCallButton from "./AiVerifyCallButton";
import { useNavigate } from "react-router-dom";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  ShieldGlyph,
  CalendarGlyph,
  getConfirmedState,
  STATE_COLOR,
  WC_TOOLTIP_COPY,
  APPT_TOOLTIP_COPY,
  TOOLTIP_SX,
  formatVerificationDate,
} from "./detail-card/VerificationIcons";
import { getClientDetailsByToken } from "../redux/ducks/organization";
import { getDoctorLogs } from "../redux/ducks/aiVerificationCall";
import NpdDetailsTab from "./NpdDetailsTab";

function normalizeToE164(rawNumber) {
  if (!rawNumber) return rawNumber;
  const trimmed = String(rawNumber).trim();
  if (trimmed.startsWith("+")) return trimmed;
  const digitsOnly = trimmed.replace(/\D/g, "");
  if (digitsOnly.length === 10) return `+1${digitsOnly}`;
  if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) return `+${digitsOnly}`;
  return trimmed;
}

function formatCallDate(isoString) {
  if (!isoString) return "";
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function formatCallTime(isoString) {
  if (!isoString) return "";
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

// Matches the reference "Last call status" card — both verification
// signals shown side by side (WC + 1M Apmt), using the same glyphs and
// colors as the WC / 1M Apmt columns in the results table
// (VerificationIcons.jsx), so a doctor's status reads identically in
// both places instead of two independently-maintained visual languages.
// No 4th state: if there's no call yet, the card doesn't render at all
// (handled at the call site below).
const WC_STATUS_LABEL = { yes: "Accepts", no: "Does not accept", "no-response": "Pending" };
const APPT_STATUS_LABEL = { yes: "Available", no: "Not available", "no-response": "Pending" };

const ProviderCard = (props) => {
  const { providerSearchData } = useSelector((state) => state.provider);
  const { loginAuth } = useSelector((state) => state.auth);
  const { loggedInUserByToken } = useSelector((state) => state.user);
  const { doctorLogsByPhone } = useSelector((state) => state.aiVerificationCall);
  const { clientDetail } = useSelector((state) => state.organization);
  const dispatch = useDispatch();
  const ref = useRef(null);
  const navigate = useNavigate();
  const [openReferral, setOpenReferral] = useState(false);

  // Last call status — for the "Last call status" card in the side
  // panel. Uses the real getDoctorLogs Redux action (hits the actual
  // mci-server backend), already scoped to this one doctor and sorted
  // newest-first.
  const normalizedPhone = normalizeToE164(props?.details?.telephoneNumber);
  const mostRecentCall =
    (normalizedPhone && doctorLogsByPhone[normalizedPhone]?.[0]) || null;

  useEffect(() => {
    if (!normalizedPhone) return;
    dispatch(getDoctorLogs(normalizedPhone));
  }, [dispatch, normalizedPhone]);

  // The org's real name (clientDetail.name) previously only loaded on
  // the Profile page — meaning a call placed without ever visiting
  // Profile first would send an empty organizationName, and the backend
  // would record it permanently as "Unknown Org". Fetching it directly
  // here removes that dependency entirely. Guarded so it only fetches
  // once, not on every render, and skips re-fetching if already loaded
  // (e.g. by Profile).
  useEffect(() => {
    if (!clientDetail?.name) {
      dispatch(getClientDetailsByToken());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- AI Verify Call: organization/user identity for billing attribution ---
  // CONFIRMED via real console output (loggedInUserByToken): the correct
  // fields are clientId (org ID), id (user ID), displayName (user name).
  // No organization NAME field exists on loggedInUserByToken at all —
  // the real name lives in a separate Redux slice
  // (state.organization.clientDetail.name, same one Profile.jsx reads).
  const currentUser = loggedInUserByToken || loginAuth || {};
  const currentOrganizationId = currentUser?.clientId || "";
  const currentUserId = currentUser?.id || "";
  const currentUserName = currentUser?.displayName || currentUser?.email || "";
  const currentOrganizationName = clientDetail?.name || "";

  // Two-tab view replacing the old separate NPD modal: "Practice
  // patterns vs Peers" (the existing metrics grid) and "NPD details"
  // (the CMS record, now shown inline instead of popping a dialog).
  const [activeTab, setActiveTab] = useState("patterns");

  // Sliding active-tab pill: measured (not guessed) from the real
  // button's own box, since the two tab labels are different lengths
  // ("Practice patterns vs Peers" vs "NPD details") - a fixed-width pill
  // would either clip the longer label or leave a gap under the
  // shorter one.
  const tabBarRef = useRef(null);
  const patternsTabRef = useRef(null);
  const npdTabRef = useRef(null);
  const [pillRect, setPillRect] = useState({ left: 0, width: 0, top: 0, height: 0 });

  useLayoutEffect(() => {
    const recalcPill = () => {
      const barEl = tabBarRef.current;
      const activeEl = activeTab === "patterns" ? patternsTabRef.current : npdTabRef.current;
      if (!barEl || !activeEl) return;
      const barRect = barEl.getBoundingClientRect();
      const elRect = activeEl.getBoundingClientRect();
      setPillRect({
        left: elRect.left - barRect.left,
        width: elRect.width,
        top: elRect.top - barRect.top,
        height: elRect.height,
      });
    };
    recalcPill();
    window.addEventListener("resize", recalcPill);
    return () => window.removeEventListener("resize", recalcPill);
  }, [activeTab]);

  const { costPerPatientMax, costPerPatientMin } = providerSearchData;
  const { setOpen, setCurrentNPI } = props;

  const sendEmail = () => {
    const subject = `Provider Info`;
    let body = "";
    body +=
      props?.details?.title +
      "%0D%0A%20%20%20" +
      props?.details?.specialty +
      "%0D%0A%20%20%20" +
      props?.details?.NPI +
      "%0D%0A%20%20%20License:%20" +
      props?.details?.licenseNumber +
      "%0D%0A%20%20%20Phone Number:%20" +
      props?.details?.telephoneNumber +
      "%0D%0A%20%20%20Fax:%20" +
      props?.details?.faxNumber +
      "%0D%0A%20%20%20Address:%20" +
      props?.details?.address.charAt(0).toUpperCase() +
      props?.details?.address.slice(1).toLowerCase();
    body += "%0D%0A------------------------------------------%0D%0A";
    window.open(`mailto:""?&subject=${subject}&body=${body}`, "_blank");
  };

  const getClassName = (item) => {
    if (item?.modusSafetyScore >= 60 && item?.procedureCount >= 2000)
      return "modus_safetfy_color_green";
    else if (item?.procedureCount < 2000) return "modus_safetfy_color_gray";
    else if (
      item?.modusSafetyScore >= 30 &&
      item?.modusSafetyScore <= 59 &&
      item?.procedureCount >= 2000
    )
      return "modus_safetfy_color_yellow";
    else return "modus_safetfy_color_red";
  };

  const handleAISearch = () => {
    setCurrentNPI(props?.details);
    setOpen((prev) => !prev);
  };

  // Real fields only — modusSafetyScore drives both the badge color and
  // the gauge ring fill; hasRedFlag is the one real flag signal we have
  // (there's no flag *count* in the data, so we don't invent one).
  const safetyScore = props?.details?.modusSafetyScore;
  const clampedScore = Math.max(0, Math.min(100, Number(safetyScore) || 0));
  const scoreClass = getClassName(props.details);

  // Ring draws itself and the number counts up on mount, rather than
  // appearing instantly -- same idea as the marketing site's mock
  // dashboard, but on a real score this time. Unlike that mock
  // version, the ring's color here stays fixed at its real, already-
  // correct tier throughout the animation (from getClassName, real
  // business logic) -- cycling through fake intermediate tier colors
  // would incorrectly imply a live scoring process that isn't actually
  // happening; this is a known, real value simply animating into view.
  // Kept fast (600ms) since this renders on every card in a results
  // list people scroll through repeatedly all day -- the marketing
  // site's slower, more narrative pacing would feel sluggish here.
  const [ringPct, setRingPct] = useState(0);
  const [ringNum, setRingNum] = useState(0);
  // One-shot glow around the ring once the count-up lands on the real
  // number, so the eye actually notices the final value instead of the
  // animation just quietly stopping. Not set in the reduced-motion path
  // below - the ring pulse is itself a movement effect.
  const [ringDone, setRingDone] = useState(false);

  useEffect(() => {
    setRingDone(false);
    const reducedMotion = !window.matchMedia("(prefers-reduced-motion: no-preference)").matches;
    if (reducedMotion) {
      setRingPct(clampedScore);
      setRingNum(safetyScore);
      return;
    }
    const dur = 600;
    const start = performance.now();
    let rafId;
    function tick(now) {
      const p = Math.min((now - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setRingPct(clampedScore * eased);
      setRingNum(Math.round((Number(safetyScore) || 0) * eased));
      if (p < 1) {
        rafId = requestAnimationFrame(tick);
      } else {
        setRingDone(true);
      }
    }
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safetyScore]);
  return (
    <div className={classes["container"]} ref={ref}>
      <div className={classes["wrapper"]}>
        <div className={classes["doctor-detail"]}>
          <div>
            <div className={classes["title-container"]}>
              <button
                type="button"
                className={classes["title-link"]}
                onClick={() => setActiveTab("npd")}
                title="Show NPD details"
              >
                <span className={classes["title"]}>
                  {props?.details?.title}
                </span>
                <ChevronRightIcon className={classes["title-chevron"]} sx={{ fontSize: 17 }} />
              </button>
              <MdOutlineIosShare
                className={classes["share-icon"]}
                onClick={() => sendEmail()}
              />
            </div>
            <div className={classes["specialty-activity-row"]}>
              <p className={classes["specialty"]}>{props?.details?.specialty}</p>
              <div className={classes["activity-div"]}>
                <span className={classes["activity"]}>Billing activity</span>
                <span className={classes["procedureCount"]}>
                  {props?.details?.procedureCount}
                </span>
              </div>
            </div>
          </div>

          <div className={classes["doctor-rating"]}>
            <span className={classes["rating"]}>
              Modus rating
              <Tooltip
                title="Our ART, SUBJECTIVE OPINION, that we form based on the metrics extracted from data"
                placement="right"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: "#ffffff",
                      color: "#1c1c1e",
                      border: "1px solid #e5e7eb",
                      boxShadow: "0 10px 24px rgba(10, 21, 38, 0.14)",
                      fontSize: "12.5px",
                      lineHeight: 1.5,
                      padding: "10px 13px",
                      borderRadius: "9px",
                      maxWidth: 230,
                    },
                  },
                  arrow: {
                    sx: {
                      color: "#ffffff",
                      "&::before": { border: "1px solid #e5e7eb" },
                    },
                  },
                }}
              >
                <ErrorOutlineRoundedIcon className={classes["info-icon"]} />
              </Tooltip>
            </span>

            <div className={classes["search_score"]}>
              <div
                className={classes[scoreClass]}
                style={{ "--ring-pct": ringPct }}
              >
                <span>{ringNum}</span>
                {ringDone && <span className={classes["ring-pulse"]} />}
              </div>
              <div className={classes["ai_search"]}>
                <button
                  className={classes["ai_search_button"]}
                  onClick={() => handleAISearch()}
                >
                  Background search
                  <LaunchTwoToneIcon className={classes["external-link"]} />
                </button>
              </div>
            </div>
          </div>

          <div>
            <span className={classes["section-label"]}>Peer feedback</span>
            <div className={classes["feedback-row"]}>
              <LikeDislike npi={props?.details?.npi} />
            </div>
          </div>

          <div className={classes["actions-section"]}>
            {loginAuth.isClaimEnabled || loggedInUserByToken?.isClaimEnabled ? (
              <Button
                variant="contained"
                startIcon={<NoteAddIcon />}
                onClick={() => setOpenReferral(true)}
                className={classes["create-referral-btn"]}
              >
                Create referral
              </Button>
            ) : null}

            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <AiVerifyCallButton
                doctorName={props?.details?.title}
                doctorSpecialty={props?.details?.specialty}
                doctorFaxNumber={props?.details?.faxNumber}
                doctorAddress={props?.details?.shortAddress}
                initialPhoneNumber={props?.details?.telephoneNumber}
                organizationId={currentOrganizationId}
                organizationName={currentOrganizationName}
                userId={currentUserId}
                userName={currentUserName}
              />
            </div>

            {/* Last call status — only renders when a call actually
                exists for this doctor; blank otherwise, per the
                reference design and explicit requirement. Shows both
                verification signals (WC + 1M Apmt) side by side, using
                the same icons/colors as the WC / 1M Apmt columns in the
                results table, so a doctor's status reads identically in
                both places. Field names match the real backend's actual
                response shape (acceptsWorkersComp / appointmentWithinMonth
                / createdAtUtc), confirmed via real Swagger testing. */}
            {mostRecentCall && (() => {
              const wcState = getConfirmedState(mostRecentCall.acceptsWorkersComp);
              const apptState = getConfirmedState(mostRecentCall.appointmentWithinMonth);
              const callDate = formatCallDate(mostRecentCall.createdAtUtc);
              const callTime = formatCallTime(mostRecentCall.createdAtUtc);
              const verifiedOnDate = formatVerificationDate(mostRecentCall.createdAtUtc);
              const wcCopy = WC_TOOLTIP_COPY[wcState];
              const apptCopy = APPT_TOOLTIP_COPY[apptState];
              return (
                <div style={{ marginTop: 4 }}>
                  <p style={{
                    fontSize: 10, fontWeight: 700, color: "#8a93a3", margin: "0 0 8px 0",
                    textTransform: "uppercase", letterSpacing: "0.05em",
                  }}>
                    Last call status
                  </p>
                  <div style={{
                    display: "flex", alignItems: "center", gap: 14,
                    background: "#eef1f4", borderRadius: 14, padding: "12px 16px",
                  }}>
                    <Tooltip
                      arrow
                      componentsProps={TOOLTIP_SX}
                      title={
                        <>
                          <b>{wcCopy.title}</b>
                          {wcCopy.body}
                          {verifiedOnDate ? ` (${verifiedOnDate})` : ""}
                        </>
                      }
                    >
                      <div className={classes["verif-pop"]} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                        <ShieldGlyph state={wcState} size={20} />
                        <span style={{ fontSize: 9.5, fontWeight: 700, color: STATE_COLOR[wcState], letterSpacing: "0.02em", whiteSpace: "nowrap" }}>
                          {WC_STATUS_LABEL[wcState].toUpperCase()}
                        </span>
                      </div>
                    </Tooltip>
                    <div style={{ width: 1, alignSelf: "stretch", background: "#d7dce1" }} />
                    <Tooltip
                      arrow
                      componentsProps={TOOLTIP_SX}
                      title={
                        <>
                          <b>{apptCopy.title}</b>
                          {apptCopy.body}
                          {verifiedOnDate ? ` (${verifiedOnDate})` : ""}
                        </>
                      }
                    >
                      <div
                        className={classes["verif-pop"]}
                        style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, animationDelay: "90ms" }}
                      >
                        <CalendarGlyph state={apptState} size={18} />
                        <span style={{ fontSize: 9.5, fontWeight: 700, color: STATE_COLOR[apptState], letterSpacing: "0.02em", whiteSpace: "nowrap" }}>
                          {APPT_STATUS_LABEL[apptState].toUpperCase()}
                        </span>
                      </div>
                    </Tooltip>
                    <div style={{ flex: 1 }} />
                    {(callDate || callTime) && (
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        {callDate && <p style={{ fontSize: 11, color: "#8a93a3", margin: 0, lineHeight: 1.4 }}>{callDate}</p>}
                        {callTime && <p style={{ fontSize: 11, color: "#8a93a3", margin: 0, lineHeight: 1.4 }}>{callTime}</p>}
                      </div>
                    )}
                  </div>
                  <div
                    onClick={() => {
                      // Remember which doctor was expanded (by NPI — the
                      // same unique key DetailCard.jsx's expandedRow
                      // state uses, not name, since two doctors can
                      // share a first name), so the search results page
                      // can restore this exact card when we navigate
                      // back — otherwise it's lost, since navigating
                      // away fully unmounts the results page.
                      sessionStorage.setItem("expandedDoctorNpi", props?.details?.npi || "");
                      navigate(
                        `/base/doctor-logs?phone=${encodeURIComponent(props?.details?.telephoneNumber || "")}&name=${encodeURIComponent(props?.details?.title || "")}`
                      );
                    }}
                    className={classes["view-logs-link"]}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
                      fontSize: 10.5, fontWeight: 700, color: "#075c53",
                      marginTop: 10, cursor: "pointer",
                    }}
                  >
                    View all call logs
                    <ChevronRightIcon className={classes["view-logs-chevron"]} sx={{ fontSize: 12 }} />
                  </div>
                </div>
              );
            })()}
          </div>

          {/* New component for the comment section feature*/}
          <div>
            <ProviderComment npi={props?.details?.npi} />
          </div>
        </div>
        <div className={classes["metrics-col"]}>
          <div className={classes["metrics-heading"]}>
            <div className={classes["tab-bar"]} ref={tabBarRef}>
              <span
                className={classes["tab-pill"]}
                style={{ left: pillRect.left, width: pillRect.width, top: pillRect.top, height: pillRect.height }}
              />
              <button
                type="button"
                ref={patternsTabRef}
                className={`${classes["tab-btn"]} ${activeTab === "patterns" ? classes["active"] : ""}`}
                onClick={() => setActiveTab("patterns")}
              >
                Practice patterns vs Peers
              </button>
              <button
                type="button"
                ref={npdTabRef}
                className={`${classes["tab-btn"]} ${activeTab === "npd" ? classes["active"] : ""}`}
                onClick={() => setActiveTab("npd")}
              >
                NPD details
              </button>
            </div>
            {activeTab === "npd" && (
              <span className={classes["tab-meta"]}>Sourced from CMS NPD</span>
            )}
          </div>

          {activeTab === "patterns" && (
            <div className={classes["tab-panel"]}>
              <SliderView
                MaxValue={costPerPatientMax}
                MinValue={costPerPatientMin}
                providerDetails={props?.details}
              />
            </div>
          )}

          {activeTab === "npd" && (
            <div className={classes["tab-panel"]}>
              <NpdDetailsTab npi={props?.details?.npi} fallbackName={props?.details?.title} />
            </div>
          )}
        </div>
      </div>

      {loginAuth?.isClaimEnabled || loggedInUserByToken?.isClaimEnabled ? (
        <ReferralPopup
          open={openReferral}
          onClose={() => setOpenReferral(false)}
          providerDetails={props?.details}
        />
      ) : null}
    </div>
  );
};

export default ProviderCard;